// Operator console: dashboard metrics, workspace page, billing lists,
// manual billing and platform settings (routes/platform/console.php).
import { formatMoney, type Envelope, type PageMeta, type PlatformTenant } from '@bixo/ui'
import { useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import type { SubscriptionInfo, SubscriptionStatus, Tenant } from './types'

const data = <T>(promise: Promise<{ data: Envelope<T> }>): Promise<T> => promise.then((r) => r.data.data)

export interface ConsoleEvent {
  type: 'signup' | 'payment' | 'payment_failed' | 'suspended'
  at: string
  title: string
  detail: string | null
  tenant: { id: string; name: string } | null
  amount: string | null
}

export interface ConsoleDashboard {
  mrr: string
  arr: string
  paying: number
  arpa: string
  revenue_this_month: string
  revenue_last_month: string
  revenue_change_pct: string | null
  conversions_this_month: number
  churned_this_month: number
  churn_rate_pct: number
  trials: { active: number; ending_7d: number; expired: number }
  failed_payments_30d: number
  in_grace: number
  outstanding: string
  overdue_invoices: number
  plan_mix: { plan: string; name: string; count: number }[]
  tenants: {
    total: number
    active: number
    provisioning: number
    suspended: number
    failed: number
    trialing: number
  }
  trials_ending_7d: number
  signups_30d: { date: string; count: number }[]
  revenue_12m: { month: string; total: string }[]
  events: ConsoleEvent[]
  recent_tenants: Tenant[]
}

export type InvoiceStatus = 'paid' | 'due' | 'void' | 'issued'

export interface ConsoleInvoice {
  id: number
  number: string
  type: 'invoice' | 'credit_note'
  tenant: { id: string; name: string; slug: string }
  plan_key: string | null
  billing_cycle: string | null
  lines: { description: string; amount: string }[] | null
  period_start: string | null
  period_end: string | null
  subtotal: string
  cgst: string
  sgst: string
  igst: string
  total: string
  amount_paid: string
  balance: string
  status: InvoiceStatus
  overdue: boolean
  source: 'gateway' | 'manual' | 'credit_note'
  gateway_payment_id: string | null
  credit_note_for_id: number | null
  notes: string | null
  void_reason: string | null
  issued_at: string
  due_date: string | null
  paid_at: string | null
  emailed_at: string | null
}

export interface InvoiceTotals {
  taxable: string
  tax: string
  total: string
  outstanding: string
  credited: string
}

export interface ConsolePayment {
  id: number
  tenant: { id: string; name: string; slug: string }
  invoice: { id: number; number: string } | null
  source: 'gateway' | 'manual'
  mode: string
  reference: string | null
  amount: string
  paid_on: string
  notes: string | null
  recorded_at: string
}

export interface ConsoleSubscription {
  id: number
  tenant: { id: string; name: string; slug: string; status: string; owner_email: string | null }
  plan_key: string
  plan_name: string
  billing_cycle: 'monthly' | 'yearly'
  amount: string | null
  status: SubscriptionStatus
  gateway: string
  gateway_subscription_id: string | null
  checkout_url: string | null
  current_period_start: string | null
  current_period_end: string | null
  grace_ends_at: string | null
  cancelled_at: string | null
  last_dunning_at: string | null
  abandoned?: boolean
  started_at?: string | null
  next_reminder_at?: string
  suspends_at?: string | null
  outstanding?: string
}

export interface ConsoleTrial {
  tenant: { id: string; name: string; slug: string; status: string; owner_email: string | null }
  trial_ends_at: string | null
  days_left: number | null
  intended_plan: string | null
  subscription_status: SubscriptionStatus | null
  grace_ends_at: string | null
  created_at: string
}

export interface TenantOverview {
  tenant: PlatformTenant & {
    plan_name?: string
    trial_ends_at?: string | null
    subscription_status?: SubscriptionStatus | null
    business_template?: string | null
    provisioned_at: string | null
    suspended_at: string | null
    suspension_reason: string | null
    billing_gstin: string | null
    billing_state_code: string | null
    domains: string[]
  }
  owner: {
    name: string | null
    email: string | null
    phone: string | null
    last_login_at: string | null
  } | null
  subscription: SubscriptionInfo | null
  invoices: ConsoleInvoice[]
  payments: {
    id: number
    source: string
    mode: string
    reference: string | null
    amount: string
    paid_on: string
    invoice: { id: number; number: string } | null
  }[]
  billing_events: {
    id: number
    type: string
    gateway: string
    at: string | null
    processed: boolean
    error: string | null
  }[]
  usage: { key: string; label: string; used: number | null; limit: number | null }[]
  notes: { id: number; body: string; author: string | null; at: string }[]
  audit: {
    id: number
    user_name: string | null
    action: string
    description: string | null
    created_at: string
  }[]
}

export interface SettingValue {
  value: string | number | boolean | null
  source: 'console' | 'env' | null
}
export interface SecretValue {
  set: boolean
  masked: string | null
  source: 'console' | 'env' | null
}

export interface PlatformSettingsData {
  values: Record<string, SettingValue | SecretValue>
  webhook_url: string
  webhook_events: string[]
  providers: string[]
  environment: string
}

export type SettingsGroup = 'gateway' | 'seller' | 'billing' | 'mail' | 'apps'

export interface ListQuery {
  page?: number
  search?: string
  [key: string]: string | number | undefined
}

interface Page<T, M = object> {
  data: T[]
  meta: PageMeta & M
}

const clean = (q: ListQuery): ListQuery =>
  Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== '')) as ListQuery

export const consoleApi = {
  dashboard: () => data<ConsoleDashboard>(api.get('/dashboard')),
  overview: (tenantId: string) => data<TenantOverview>(api.get(`/tenants/${tenantId}/overview`)),
  addNote: (tenantId: string, body: string) =>
    data<TenantOverview['notes']>(api.post(`/tenants/${tenantId}/notes`, { body })),

  invoices: (q: ListQuery) =>
    api
      .get<Page<ConsoleInvoice, { totals: InvoiceTotals }>>('/billing/invoices', { params: clean(q) })
      .then((r) => r.data),
  invoicesCsvUrl: (q: ListQuery) =>
    `/papi/v1/billing/invoices.csv?${new URLSearchParams(clean(q) as Record<string, string>).toString()}`,
  payments: (q: ListQuery) =>
    api
      .get<Page<ConsolePayment, { total_amount: string }>>('/billing/payments', { params: clean(q) })
      .then((r) => r.data),
  subscriptions: (q: ListQuery) =>
    api.get<Page<ConsoleSubscription>>('/billing/subscriptions', { params: clean(q) }).then((r) => r.data),
  checkouts: (q: ListQuery) =>
    api.get<Page<ConsoleSubscription>>('/billing/checkouts', { params: clean(q) }).then((r) => r.data),
  trials: (q: ListQuery) =>
    api.get<Page<ConsoleTrial>>('/billing/trials', { params: clean(q) }).then((r) => r.data),
  dunning: (q: ListQuery) =>
    api.get<Page<ConsoleSubscription>>('/billing/dunning', { params: clean(q) }).then((r) => r.data),

  raiseInvoice: (
    tenantId: string,
    body:
      | {
          kind: 'plan'
          plan: string
          cycle: 'monthly' | 'yearly'
          period_start?: string
          due_date?: string
          notes?: string
          send?: boolean
        }
      | {
          kind: 'custom'
          lines: { description: string; amount: string }[]
          due_date?: string
          notes?: string
          send?: boolean
        },
  ) => data<ConsoleInvoice>(api.post(`/tenants/${tenantId}/invoices`, body)),
  recordPayment: (
    invoiceId: number,
    body: { mode: string; reference?: string; paid_on: string; amount?: string; notes?: string },
  ) => data<ConsoleInvoice>(api.post(`/invoices/${invoiceId}/payments`, body)),
  voidInvoice: (invoiceId: number, reason: string) =>
    data<ConsoleInvoice>(api.post(`/invoices/${invoiceId}/void`, { reason })),
  creditNote: (invoiceId: number, body: { reason: string; amount?: string }) =>
    data<ConsoleInvoice>(api.post(`/invoices/${invoiceId}/credit-note`, body)),
  emailInvoice: (invoiceId: number, to?: string) =>
    data<{ queued: boolean; to: string; mail_configured: boolean }>(
      api.post(`/invoices/${invoiceId}/email`, to ? { to } : {}),
    ),

  assignPlan: (
    tenantId: string,
    body: { plan: string; cycle: 'monthly' | 'yearly'; complimentary?: boolean },
  ) => data<SubscriptionInfo>(api.post(`/tenants/${tenantId}/plan`, body)),

  settings: () => data<PlatformSettingsData>(api.get('/settings')),
  saveSettings: (group: SettingsGroup, body: Record<string, unknown>) =>
    data<PlatformSettingsData>(api.put(`/settings/${group}`, body)),
  testGateway: () => data<{ ok: boolean; message: string }>(api.post('/settings/gateway/test')),
  testMail: (to: string) => data<{ ok: boolean; message: string }>(api.post('/settings/mail/test', { to })),
  createPlan: (body: Record<string, unknown>) => data<unknown>(api.post('/plans', body)),
}

export const ck = {
  dashboard: ['console', 'dashboard'] as const,
  overview: (id: string) => ['console', 'overview', id] as const,
  list: (kind: string, q: ListQuery) => ['console', kind, q] as const,
  settings: ['console', 'settings'] as const,
}

export const PAYMENT_MODES: { value: string; label: string }[] = [
  { value: 'bank_transfer', label: 'Bank transfer (NEFT/RTGS/IMPS)' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
]

export const modeLabel = (mode: string): string =>
  mode === 'razorpay'
    ? 'Razorpay'
    : (PAYMENT_MODES.find((m) => m.value === mode)?.label.split(' (')[0] ?? mode)

export const today = (): string => new Date().toLocaleDateString('en-CA')

export const rupees = (value: string | number | null | undefined): string => formatMoney(String(value ?? '0'))

/** Invalidate every console list, the dashboard and workspace lists after a billing change. */
export function useRefreshBilling(): () => Promise<void> {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({
      predicate: (q) => ['console', 'subscription', 'tenants'].includes(String(q.queryKey[0])),
    })
}
